import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

interface EmailPreviewSectionProps {
  flowType: "VIDEO" | "WIDGET";
  messageToCustomer: string;
}

const EmailPreviewSection = ({ flowType, messageToCustomer }: EmailPreviewSectionProps) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const faqItems = [
    {
      question: t("What can you see in my recording?"),
      answer: t("We only see what appears on the screen while recording is active, plus optional microphone audio if enabled."),
    },
    {
      question: t("Is my data safe?"),
      answer: t("Yes. Recordings are encrypted in transit and at rest, and are only accessible to authorized support staff."),
    },
    {
      question: t("Do I need to install anything?"),
      answer: t("No installation is needed. The recording runs directly in your browser using a secure web link."),
    },
    {
      question: t("What if I accidentally show personal information?"),
      answer: t("If this happens, let us know right away. We can remove the recording and request a new one after you blur or hide sensitive content."),
    },
    {
      question: t("Who can see my recording?"),
      answer: t("Only the support team assigned to your case can access it. It is not shared publicly and is deleted after resolution."),
    },
  ];

  return (
    <Box
      sx={{
        mt: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        backgroundColor: theme.palette.background.default,
        p: 1.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t("Email Preview")}
      </Typography>
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          backgroundColor: theme.palette.background.paper,
          maxHeight: 520,
          overflowY: "auto",
        }}
      >
        {/* Email Header */}
        <Box
          sx={{
            background: theme.palette.primary.dark,
            color: "#fff",
            textAlign: "center",
            px: 2,
            py: 3,
          }}
        >
          <Typography sx={{ fontSize: 26, fontWeight: 800, mb: 1, lineHeight: 1 }}>
            Sniffer
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("Help us fix your issue")}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.95 }}>
            {t("It takes less than 30 seconds")}
          </Typography>
        </Box>

        {/* Email Body */}
        <Box sx={{ p: 2 }}>
          {/* Support Message */}
          <Box
            sx={{
              mb: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1.25,
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>{t("From support")}:</strong>{" "}
              {`"${messageToCustomer || t("Help us understand your issue better by recording your screen.")}"`}
            </Typography>
          </Box>

          {/* How it works */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("How it works")}:
          </Typography>
          {flowType === "VIDEO" ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                1. {t("Click the button below.")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                2. {t("Your browser asks permission to share your screen. Select the tab where the issue happens.")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                3. {t("Show the problem, then click Stop and Submit.")}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                1. {t("Click the button below to visit the page.")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                2. {t("A feedback widget will appear on the page.")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                3. {t("Use the widget to capture screenshots, record your screen, and describe the issue.")}
              </Typography>
            </>
          )}

          {/* CTA Button */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              disableElevation
              sx={{
                py: 1.2,
                mb: 2,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.dark,
                "&:hover": { backgroundColor: theme.palette.primary.main },
                color: "#fff",
              }}
            >
              {flowType === "VIDEO" ? t("Record My Screen") : t("Open Feedback Page")}
            </Button>
          </Box>

          {/* Privacy Notice */}
          <Box
            sx={{
              mb: 1.5,
              border: `1px solid ${theme.palette.success.light}`,
              borderRadius: 1,
              p: 1.25,
              backgroundColor: theme.palette.success.light + "20",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>{t("Your privacy is protected")}.</strong>{" "}
              {t("Only the assigned support team can view your recording. It is encrypted and automatically deleted after the issue is resolved.")}
            </Typography>
          </Box>

          {/* Trust Badges */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">{t("Encrypted")}</Typography>
            <Typography variant="caption" color="text.secondary">{t("No install")}</Typography>
            <Typography variant="caption" color="text.secondary">{t("Support only")}</Typography>
            <Typography variant="caption" color="text.secondary">{t("Auto-deleted")}</Typography>
          </Stack>

          {/* FAQ */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Frequently Asked Questions")}
          </Typography>
          {faqItems.map((item, i) => (
            <Box
              key={i}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1.25,
                mb: i < faqItems.length - 1 ? 1 : 0.5,
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {item.question}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.answer}
              </Typography>
            </Box>
          ))}

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            {t("If you need help, reply to this email and our support team will assist you.")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EmailPreviewSection;
